/**
 * Route guards
 * Handles authentication and authorization for routes.
 *
 * ── Guest / Visitor Access ──────────────────────────────────────
 * Routes inside the /app shell can opt-in to guest access via
 * meta: { allowGuest: true }. The auth guard checks this flag and
 * allows unauthenticated navigation instead of redirecting to login.
 *
 * Currently, the catalog route (/app/catalog) uses this to enable
 * "visitor mode" — unauthenticated users can browse catalog items
 * (assortment 10001) without prices, promotions, or discounts.
 * See catalog.js store for the full visitor mode documentation.
 * ────────────────────────────────────────────────────────────────
 */
import { ROUTES } from '../../core/config/constants.js'
import logger from '../../core/services/logger.js'

const log = logger.createScoped('Router')

// Store reference - will be set by router setup
let authStoreInstance = null

/**
 * Set the auth store instance for guards to use
 * Must be called before router is used
 * @param {Object} store - Pinia auth store instance
 */
export function setAuthStore(store) {
  authStoreInstance = store
}

/**
 * Get auth store or throw if not set
 * @returns {Object}
 */
function getAuthStore() {
  if (!authStoreInstance) {
    throw new Error('Auth store not initialized. Call setAuthStore() first.')
  }
  return authStoreInstance
}

/**
 * Main authentication guard
 * Waits for auth resolution before making routing decisions
 * @param {Object} to - Target route
 * @param {Object} from - Current route
 * @returns {Promise<Object|boolean|undefined>}
 */
export async function authGuard(to, _from) {
  const authStore = getAuthStore()

  // Wait for any in-flight auth resolution (initial bootstrap OR a subsequent
  // login/logout event) before making routing decisions. waitForAuthResolution
  // is a no-op when nothing is pending, so this is safe to always call.
  await authStore.waitForAuthResolution()

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
  const guestOnly = to.matched.some((record) => record.meta.guestOnly)
  const isAuthenticated = authStore.isAuthenticated

  log.debug(`Navigation check: ${to.path}`, {
    requiresAuth,
    guestOnly,
    isAuthenticated,
    authData: authStore.user.authData ? 'object' : authStore.user.authData,
  })

  // Allow guest access to specific routes (e.g., public catalog)
  const allowGuest = to.matched.some((record) => record.meta.allowGuest)
  if (requiresAuth && !isAuthenticated && allowGuest) {
    log.info('Allowing guest access', { path: to.path })
    return true
  }

  // Route requires authentication but user is not authenticated
  if (requiresAuth && !isAuthenticated) {
    log.info('Redirecting to login - authentication required')
    return {
      name: ROUTES.LOGIN,
      query: { redirect: to.fullPath },
    }
  }

  // Route is for guests only but user is authenticated
  if (guestOnly && isAuthenticated) {
    const role = authStore.userRole
    if (role === 'admin' || role === 'agent') {
      log.info('Redirecting to agents portal - user already authenticated', { role })
      return { name: ROUTES.AGENTS_PORTAL }
    }
    log.info('Redirecting to catalog - user already authenticated')
    return { name: ROUTES.CATALOG }
  }

  // Allow navigation
  return true
}

/**
 * Check if user has required role
 * Uses userRole getter which prefers profile.role over authData.role
 * @param {Array<string>} allowedRoles
 * @returns {Function} Guard function
 */
export function roleGuard(allowedRoles) {
  return async (_to, _from) => {
    const authStore = getAuthStore()

    // Wait for any in-flight auth resolution (includes profile + client fetch)
    await authStore.waitForAuthResolution()

    // Get user role from store getter (prefers profile.role, falls back to authData.role)
    const userRole = authStore.userRole

    if (!userRole || !allowedRoles.includes(userRole)) {
      log.warn('Access denied - insufficient permissions', { userRole, allowedRoles })
      return { name: ROUTES.CATALOG }
    }

    return true
  }
}
